import { SquareDashed, type LucideIcon } from "lucide-react";

import { Button as BaseButton } from "@/shared/components/primitives/button";
import type { ButtonProps as BaseButtonProps } from "@/shared/components/primitives/button";
import { Spinner } from "@/shared/components/primitives/spinner";
import { cn } from "@/shared/lib/utils";

export interface IconButtonProps extends BaseButtonProps {
  size?: "default" | "xs" | "sm" | "lg";
  /**
   * The icon that communicates the action. Always provide one — the action must be self-evident from the icon
   * alone since there is no visible label. Use recognizable, single-purpose icons (e.g. `<Pencil>` for edit,
   * `<Trash2>` for delete). Pair with `aria-label` so screen readers get a text equivalent.
   */
  icon?: LucideIcon;
  /**
   * Set to `true` while an async operation is in progress. Disables the button so users cannot trigger the
   * action twice.
   */
  loading?: boolean;
}

const buttonIconSizeMap: Record<string, "icon" | "icon-xs" | "icon-sm" | "icon-lg"> = {
  default: "icon",
  xs: "icon-xs",
  sm: "icon-sm",
  lg: "icon-lg",
};

/**
 * Icon-only button for compact or repeated actions where a text label would add visual noise — e.g. toolbar
 * controls, table row actions, or actions inside a card. Always include an `aria-label` for accessibility.
 *
 * When the action needs a visible label, use `TenkoButton` instead.
 */
export function TenkoIconButton({
  icon: Icon = SquareDashed,
  loading = false,
  size = "default",
  disabled,
  className,
  ...props
}: IconButtonProps) {
  return (
    <BaseButton
      disabled={disabled || loading}
      className={cn(className)}
      size={buttonIconSizeMap[size]}
      {...props}
    >
      {loading ? <Spinner /> : <Icon />}
    </BaseButton>
  );
}
